import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { fetch as axios } from '../../axios_config';

import { handleRejectedResponse } from './utils';

const NODE = process.env.OPULENT_NODE_API_URL;

/* ============================================================================
 * Dashboard data thunks — one endpoint per dashboard section ("separate API
 * section-wise"). Each thunk is thin: it calls its endpoint and returns the raw
 * response body; the render-shape mapping lives in ./dashboardMappers so the
 * thunks match the rest of the app's slice convention (cf. packing-stats.js).
 *
 * Sales/products hit the legacy reports API (baseURL = API_BASE_URL, dates
 * DD/MM/YYYY). Everything else hits the inbound /v2 API (OPULENT_NODE_API_URL,
 * dates YYYY-MM-DD). Both run on the same shared axios instance, which carries
 * the Bearer token, so tenant scope (user_id / warehouse_id) is server-derived.
 * ========================================================================== */

// 1+2. Sales KPIs + trend — aggregated by the inbound API over amazon_order_records_v2
// (server buckets by day/week/month and computes the previous window for deltas).
export const FetchDashboardSales = createAsyncThunk(
  'dashboard/sales',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/dashboard/sales`, { params: data });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Sales', rejectWithValue);
    }
  }
);

// 3. Top products / best-selling SKUs (inbound API).
export const FetchDashboardTopProducts = createAsyncThunk(
  'dashboard/top-products',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/dashboard/top-products`, { params: data });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Top Products', rejectWithValue);
    }
  }
);

// 4+5. Inventory / listings counts.
export const FetchDashboardSkuStats = createAsyncThunk(
  'dashboard/sku-stats',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/skus/stats`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Sku Stats', rejectWithValue);
    }
  }
);

// 6. Work orders by status. Uses the seller listing (user-work-orders) which INCLUDES Draft in
// statusCounts (the warehouse listing excludes Draft, which would force the Draft pill to 0).
export const FetchDashboardWorkOrders = createAsyncThunk(
  'dashboard/work-orders',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/wo/user-work-orders`, {
        params: { page: { pageSize: 1, pageNumber: 1 } }
      });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Work Orders', rejectWithValue);
    }
  }
);

// 7. Purchase orders (count, $, status breakdown) — single aggregate query (no bulk row fetch).
export const FetchDashboardPurchaseOrders = createAsyncThunk(
  'dashboard/purchase-orders',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/dashboard/po-summary`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Purchase Orders', rejectWithValue);
    }
  }
);

// 9. Catalog upload progress — the catalogs uploaded in /sourcing (catalog_v2 with progress +
// saved/matched/unmatched counts), same source as the Sourcing page's "upload new catalog" list.
export const FetchDashboardUploads = createAsyncThunk(
  'dashboard/uploads',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/catalog/get-catalogs`, {
        params: { sortField: 'id', sortOrder: 'desc', page: 1, limit: 20 }
      });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Uploads', rejectWithValue);
    }
  }
);

// 11a. Warehouse stage tiles (throughput snapshot).
export const FetchDashboardTileStats = createAsyncThunk(
  'dashboard/tile-stats',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/packing-stats/tile-stats`, { params: {} });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Tile Stats', rejectWithValue);
    }
  }
);

// 11b. Warehouse throughput time-series — SELLER-SCOPED (received against the seller's POs, prepped
// for the seller's own SKUs). Separate from the warehouse-scoped /packing-stats/graph-stats.
export const FetchDashboardThroughput = createAsyncThunk(
  'dashboard/throughput',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/dashboard/throughput`, { params: data });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Throughput', rejectWithValue);
    }
  }
);

// 11c. Inventory-by-location summary + on-hand total (new aggregate endpoint).
export const FetchDashboardInventoryByLocation = createAsyncThunk(
  'dashboard/inventory-by-location',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/location-management/inventory-summary`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Inventory By Location', rejectWithValue);
    }
  }
);

// 10. Sourcing DB saved vs unsaved (new aggregate endpoint).
export const FetchDashboardSourcingSplit = createAsyncThunk(
  'dashboard/sourcing-split',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/catalog/sourcing-split`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard Sourcing Split', rejectWithValue);
    }
  }
);

// 8. PO ordered-vs-invoiced time-series (period-scoped, new aggregate endpoint).
export const FetchDashboardPoOrderedVsInvoiced = createAsyncThunk(
  'dashboard/po-ordered-vs-invoiced',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/po/ordered-vs-invoiced`, { params: data });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Dashboard PO Ordered Vs Invoiced', rejectWithValue);
    }
  }
);

const setFetching = (key) => (state) => {
  state[key] = true;
};
const setResolved = (key, dataKey) => (state, action) => {
  state[dataKey] = action?.payload;
  state[key] = false;
};
const setFailed = (key) => (state) => {
  state[key] = false;
};

const dashboard = createSlice({
  name: 'dashboard',
  initialState: {
    fetchingSales: false, sales: {},
    fetchingTopProducts: false, topProducts: {},
    fetchingSkuStats: false, skuStats: {},
    fetchingWorkOrders: false, workOrders: {},
    fetchingPurchaseOrders: false, purchaseOrders: {},
    fetchingUploads: false, uploads: {},
    fetchingTileStats: false, tileStats: {},
    fetchingThroughput: false, throughput: {},
    fetchingInventoryByLocation: false, inventoryByLocation: {},
    fetchingSourcingSplit: false, sourcingSplit: {},
    fetchingPoOrderedVsInvoiced: false, poOrderedVsInvoiced: {}
  },
  reducers: {},
  extraReducers: {
    [FetchDashboardSales.pending]: setFetching('fetchingSales'),
    [FetchDashboardSales.fulfilled]: setResolved('fetchingSales', 'sales'),
    [FetchDashboardSales.rejected]: setFailed('fetchingSales'),

    [FetchDashboardTopProducts.pending]: setFetching('fetchingTopProducts'),
    [FetchDashboardTopProducts.fulfilled]: setResolved('fetchingTopProducts', 'topProducts'),
    [FetchDashboardTopProducts.rejected]: setFailed('fetchingTopProducts'),

    [FetchDashboardSkuStats.pending]: setFetching('fetchingSkuStats'),
    [FetchDashboardSkuStats.fulfilled]: setResolved('fetchingSkuStats', 'skuStats'),
    [FetchDashboardSkuStats.rejected]: setFailed('fetchingSkuStats'),

    [FetchDashboardWorkOrders.pending]: setFetching('fetchingWorkOrders'),
    [FetchDashboardWorkOrders.fulfilled]: setResolved('fetchingWorkOrders', 'workOrders'),
    [FetchDashboardWorkOrders.rejected]: setFailed('fetchingWorkOrders'),

    [FetchDashboardPurchaseOrders.pending]: setFetching('fetchingPurchaseOrders'),
    [FetchDashboardPurchaseOrders.fulfilled]: setResolved('fetchingPurchaseOrders', 'purchaseOrders'),
    [FetchDashboardPurchaseOrders.rejected]: setFailed('fetchingPurchaseOrders'),

    [FetchDashboardUploads.pending]: setFetching('fetchingUploads'),
    [FetchDashboardUploads.fulfilled]: setResolved('fetchingUploads', 'uploads'),
    [FetchDashboardUploads.rejected]: setFailed('fetchingUploads'),

    [FetchDashboardTileStats.pending]: setFetching('fetchingTileStats'),
    [FetchDashboardTileStats.fulfilled]: setResolved('fetchingTileStats', 'tileStats'),
    [FetchDashboardTileStats.rejected]: setFailed('fetchingTileStats'),

    [FetchDashboardThroughput.pending]: setFetching('fetchingThroughput'),
    [FetchDashboardThroughput.fulfilled]: setResolved('fetchingThroughput', 'throughput'),
    [FetchDashboardThroughput.rejected]: setFailed('fetchingThroughput'),

    [FetchDashboardInventoryByLocation.pending]: setFetching('fetchingInventoryByLocation'),
    [FetchDashboardInventoryByLocation.fulfilled]: setResolved('fetchingInventoryByLocation', 'inventoryByLocation'),
    [FetchDashboardInventoryByLocation.rejected]: setFailed('fetchingInventoryByLocation'),

    [FetchDashboardSourcingSplit.pending]: setFetching('fetchingSourcingSplit'),
    [FetchDashboardSourcingSplit.fulfilled]: setResolved('fetchingSourcingSplit', 'sourcingSplit'),
    [FetchDashboardSourcingSplit.rejected]: setFailed('fetchingSourcingSplit'),

    [FetchDashboardPoOrderedVsInvoiced.pending]: setFetching('fetchingPoOrderedVsInvoiced'),
    [FetchDashboardPoOrderedVsInvoiced.fulfilled]: setResolved('fetchingPoOrderedVsInvoiced', 'poOrderedVsInvoiced'),
    [FetchDashboardPoOrderedVsInvoiced.rejected]: setFailed('fetchingPoOrderedVsInvoiced')
  }
});

const { reducer } = dashboard;

export default reducer;
